/* import __COLOCATED_TEMPLATE__ from './date-selection.hbs'; */
import { Promise } from 'rsvp';
import Component from '@ember/component';
import { computed } from '@ember/object';
import { showNextModal, closeModal } from "sh-www/utils/modal-controls";

const DateSelection = Component.extend({
  tagName: '',

  /**
   * @type {Date}
   */
  startDate: null,

  /**
   * @type {Date|null}
   */
  endDate: null,

  /**
   * This is the model for the start and end
   *
   * {
   *    startAt: <Date>,
   *    endAt: <Date>
   * }
   *
   * Passed in from parent
   * @param {Object}
   */
  timeframe: undefined,

  /**
   * This callback function will be resolved with the dates selected by the popover
   *
   * Passed in from Parent.
   * @param {Function.<[startDate, endDate]>}
   */
  resolveDates: undefined,

  /**
   * This resolve function will be called when the user cancels the popover
   *
   * Passed in from Parent.
   * @param {Function}
   */
  rejectDates: undefined,

  /**
   * True if the start date must be at least now or greater.
   *
   * @param {Boolean}
   */
  isStartDateRestrictedToNow: computed(function() {
    return this.get('model.isStartDateRestrictedToNow');
  }),

  /**
   * Title of the date selection popover
   *
   * Passed in from Parent.
   * @param {string}
   */
  dateSelectionTitle: computed(function() {
    let title = this.get('model.dateSelectionTitle');
    return title ? title : '';
  }),

  /**
   * Description of what the date selection is for
   *
   * Passed in from Parent.
   * @param {string}
   */
  dateSelectionBody: computed(function() {
    let dateSelectionBody = this.get('model.dateSelectionBody');
    return dateSelectionBody ? dateSelectionBody : '';
  }),

  /**
   * The title for the extra confirmation alert.
   *
   * Will only show if isShowConfirmation is true
   *
   * Passed in from Parent.
   * @type {string}
   */
  dateSelectionConfirmationTitle: computed(function() {
    let dateSelectionConfirmationTitle = this.get('model.dateSelectionConfirmationTitle');
    return dateSelectionConfirmationTitle ? dateSelectionConfirmationTitle : '';
  }),

  /**
   * The body for the extra confirmation alert.  This supports dynamic content, so pass in a function
   * if you need to render dynamic content based on data from the first popover.
   *
   * e.g. function(startDate, endDate) { //custom confirmation text with dates selected }
   *
   * Will only show if isShowConfirmation is true
   *
   * Passed in from Parent.
   * @type {string|Function}
   */
  dateSelectionConfirmationBody: computed(function() {
    let dateSelectionConfirmationBody = this.get('model.dateSelectionConfirmationBody');
    return dateSelectionConfirmationBody ? dateSelectionConfirmationBody : '';
  }),

  /**
   * Will show the extra confirmation for approving dates if set to true
   *
   * Passed in from Parent.
   * @type {boolean}
   */
  isShowConfirmation: computed(function() {
    return !!this.get('model.isShowConfirmation');
  }),

  /**
   * @type {Date}
   */
  dateNow: computed(function() {
    let date = new Date();
    date.setHours(0,0,0,0);
    return date;
  }),

  /**
   * @type {string}
   */
  errorMessage: '',

  init() {
    this._super(...arguments);

    const startDate = this.model?.timeframe?.startAt?.setHours(0,0,0,0) ?? this.dateNow;
    const endDate = this.model?.timeframe?.endAt?.setHours(0,0,0,0) ?? null;

    this.setProperties({
      startDate,
      endDate,
    })
  },

  actions: {
    /**
     * When form is submitted
     */
    submitForm: function() {
      let startDate = this.get('startDate'),
        endDate = this.get('endDate'),
        isEndDateRequired = this.get('model.timeframe.endAt'),
        isStartDateRestrictedToNow = this.get('isStartDateRestrictedToNow');

      if (!startDate || (isEndDateRequired && !endDate)) {
        this.set('errorMessage', 'You must select both a start and an end date.');
        return;
      }

      let now = this.get('dateNow'),
        endMinDate = new Date(now.getTime() + 24 * 60 * 60 * 1000); //tomorrow
      if (isStartDateRestrictedToNow && startDate < now) {
        this.set('errorMessage', 'Start date must be today or later.');
        return;
      }

      if (isEndDateRequired && endDate < endMinDate) {
        this.set('errorMessage', 'End date must be tomorrow or later.');
        return;
      }

      if (isEndDateRequired && startDate >= endDate) {
        this.set('errorMessage', 'End date must be after start date.');
        return;
      }

      let isConfirmationNeeded = this.get('isShowConfirmation');
      if (!isConfirmationNeeded) {
        //no confirmation needed, resolve and return
        this.get('model.resolveDates')([startDate, endDate]);
        closeModal(this);
        return;
      }

      /**
       * Function to CONFIRM the saved dates in the confirmation popover
       *
       * @returns {Ember.RSVP.Promise.<boolean>}
       */
      let confirmSaveDates = () => {
        this.get('model.resolveDates')([startDate, endDate]);
        return Promise.resolve(true);
      };

      /**
       * Function to CANCEL the saved dates in the confirmation popover
       *
       * @returns {Ember.RSVP.Promise.<boolean>}
       */
      let cancelSaveDates = () => {
        this.get('actions.onCancelClicked').bind(this)();
        return Promise.resolve(true);
      };

      // This can be a function to support dynamic content, so need to compute right before showing popover.
      let dateSelectionConfirmationBody = this.get('dateSelectionConfirmationBody');
      dateSelectionConfirmationBody = typeof dateSelectionConfirmationBody === 'function' ?
        dateSelectionConfirmationBody(startDate, endDate) :
        dateSelectionConfirmationBody;

      /**
       * Data model for modal
       *
       * @type {Object}
       */
      let modalData = {
        titleText: this.get('dateSelectionConfirmationTitle'),
        bodyText: dateSelectionConfirmationBody,
        actionButtonText: 'Yes, I\'m sure.',
        cancelButtonText: 'Cancel',
        confirmAction: confirmSaveDates,
        cancelAction: cancelSaveDates
      };

      showNextModal(this, 'confirmation', modalData);
    },

    /**
     * When the form is cancelled
     */
    onCancelClicked: function() {
      this.get('model.rejectDates')();
      this.set('errorMessage', '');
      closeModal(this);
    },

    /**
     * called when a new date is selected
     *
     * @param {string} dateType     of type DateSelection.DATE_TYPE_*
     * @param {Date} value
     */
    onDateSelected: function(dateType, value) {
      this.set('errorMessage', '');
      if (dateType === DateSelection.DATE_TYPE_START) {
        this.set('startDate', value);
      } else {
        this.set('endDate', value);
      }
    }
  }
});

DateSelection.reopenClass({
  DATE_TYPE_START : 'start'
});

export default DateSelection;
